import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  fetchBillingSettings,
  fetchIntegrations,
  fetchSystemSettings,
  saveBillingSection,
  saveDashboardSettings,
  settingsKeys,
  type SettingsPatch,
} from "api/sapSettings";
import { shouldRetryRequest } from "api/apiError";
import { useAuth } from "hooks/useAuth";

/** The SAP settings page: each section loads only for the people allowed to see it. */
export const useSapSettingsData = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const canReadBilling = hasPermission("billing", "read");
  const canEditBilling = hasPermission("billing", "configure");
  const canReadSystem = hasPermission("tenantManagement", "read");
  const canEditSystem = hasPermission("tenantManagement", "configure");

  const billing = useQuery({
    queryKey: settingsKeys.billing,
    queryFn: fetchBillingSettings,
    retry: shouldRetryRequest,
    enabled: canReadBilling,
  });
  const system = useQuery({
    queryKey: settingsKeys.system,
    queryFn: fetchSystemSettings,
    retry: shouldRetryRequest,
    enabled: canReadSystem,
  });
  const integrations = useQuery({
    queryKey: settingsKeys.integrations,
    queryFn: fetchIntegrations,
    retry: shouldRetryRequest,
    enabled: canEditSystem,
  });

  const savePayee = useMutation({
    mutationFn: (patch: SettingsPatch) => saveBillingSection("payee", patch),
    onSuccess: (data) => queryClient.setQueryData(settingsKeys.billing, data),
  });
  const saveNotifications = useMutation({
    mutationFn: (patch: SettingsPatch) => saveBillingSection("notifications", patch),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.billing, data);
      // WhatsApp's status depends on the template names.
      queryClient.invalidateQueries({ queryKey: settingsKeys.integrations });
    },
  });
  const saveDashboard = useMutation({
    mutationFn: (patch: SettingsPatch) => saveDashboardSettings(patch),
    onSuccess: (data) => {
      queryClient.setQueryData(settingsKeys.system, data);
      queryClient.invalidateQueries({ queryKey: ["fleet"] });
    },
  });

  return {
    canReadBilling,
    canEditBilling,
    canReadSystem,
    canEditSystem,
    billing,
    system,
    integrations,
    savePayee,
    saveNotifications,
    saveDashboard,
  };
};
